import type { HomeResponse } from "../shared/dto";

/**
 * 🔴 F152-W4 (Pavel 2026-08-14), verbatim: "Schválený webinář = konec cesty."
 *
 * Where a journey ENDS - one derivation, because two surfaces already ask
 * (the home headline and the finished card) and the walkthrough will be third.
 *
 * `sales_only`: the four client materials are approved. There is no handover
 * and no bot, because the Buddy was never ours; the last thing that happens is
 * the client approving their webinar. The fact arrives from the server
 * (`salesMaterialsApproved`), which computes it in `buildSalesJourney` - the
 * SAME predicate that opens the handover on the full journey. It cannot be
 * recomputed from `salesSteps` here: an approved-but-unpublished sales page and
 * one still in review are both `"next"` in that list (F120's three phases), so
 * a second reader would have had to invent its own answer.
 *
 * 🔴 It may NOT stand on the sections. F134 closed S17, so `sales` is written
 * `working` by the q2 submit and nobody ever writes it `done` on this journey
 * (S20 is not run, S25 refuses it as a `protected_completion`). That is why the
 * F143 copy `home.finished.*SalesOnly` sat in both locales as unreachable dead
 * code from the day it was written.
 *
 * `full`: unchanged, deliberately. Every section done except `home` and `app`.
 * The audit (D10) recorded that this is unreachable for the full journey too,
 * for the same S17 reason; redefining THAT is a decision about the handover
 * which has not been made, and making it here as a side effect of a sales_only
 * fix would be inventing one.
 *
 * Derived, never written: a client who approved their webinar yesterday is
 * finished the moment this ships (the F137 law - a derivation is retroactive by
 * construction, so there is no sweep and no data fix).
 *
 * `undefined` reads as NOT finished: a cached payload from before this wave, or
 * a fixture that carries no sales chapter, has said nothing about the end.
 *
 * 🔴 F171 (Tomas Pohl 2026-10-06): a client who bought the payment setup has
 * one more step after the materials, "Napojení plateb", which the TEAM closes.
 * Their sales_only journey ends when BOTH are true: the webinar is approved
 * and the payments step is done. Without the step (every client before F171,
 * and everybody who did not buy it) the F152-W4 end is unchanged. The full
 * journey's end is not touched, for the reason above.
 */
export function paymentsStepPending(home: HomeResponse): boolean {
  const payments = home.salesSteps?.find((step) => step.key === "payments");
  return Boolean(payments && payments.state !== "done");
}

/** Where the journey ends; see the notes above. */
export function journeyFinished(home: HomeResponse): boolean {
  if (home.client.journeyMode === "sales_only") {
    return home.salesMaterialsApproved === true && !paymentsStepPending(home);
  }
  return home.sections.every((section) =>
    section.key === "home" || section.key === "app" || section.state === "done");
}

/**
 * 🔴 The home is showing its FINISHED face - headline, card and the ABSENCE of
 * the step counter are one state, not three conditions that can disagree.
 *
 * `hasPrimarySection` is the other half: while a section still carries the
 * client's turn, `PrimaryNextAction` draws that action and the page must not
 * congratulate over it.
 *
 * The counter needed saying out loud because of where the sales_only end sits.
 * The full journey never reached this: when its every section is done, no rail
 * step is `next` or `working`, so `primaryRailKey` finds nothing and the counter
 * is already 0. A sales_only journey ends on the four MATERIALS while the sales
 * page keeps its own phases 2 and 3 (order links, publication - F120/F137), so
 * the pointer can still name a step, and the page read "Krok 5 z 7" over the
 * headline "Prodejní balíček je hotový".
 */
export function homeShowsFinished(finished: boolean, hasPrimarySection: boolean): boolean {
  return finished && !hasPrimarySection;
}
